/**
 * How a sentence refers to a fighter (D-124).
 *
 * Every story the app writes itself — the templated fallback, the fact pages,
 * the era note — said "the lion … it … its", and that was right for 181
 * animals. Brock Lesnar is the first record that is a person: "Brock Lesnar
 * … he … his", no article, capitals kept. Three optional record fields say
 * how a name reads, and this module turns them into the words a sentence
 * frame needs, so no template spells "the" or "it" for a name again:
 *
 *   proper_noun   the name takes no article and keeps its capitals
 *                 ("Brock Lesnar"; one day "Bigfoot"). Default false.
 *   pronoun       "it" (default), "he" or "she".
 *   person        a real human being on the roster as a legend. The era note
 *                 and the prompt say so, because "lives in stories" would be a
 *                 lie about a real person — and the photo slot shows the
 *                 photograph that proves it (D-150).
 *
 * All three pronouns take third-person-singular verbs ("he is beaten", "it is
 * beaten"), which is why "they" is not offered: every frame would need a
 * second verb form. Takes anything with `name`: a full record or a roster
 * summary, so the client's preview reads the same words as the server.
 */

const PRONOUNS = {
  it: { subj: 'it', obj: 'it', poss: 'its', refl: 'itself' },
  he: { subj: 'he', obj: 'him', poss: 'his', refl: 'himself' },
  she: { subj: 'she', obj: 'her', poss: 'her', refl: 'herself' },
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

export const isPerson = (x) => x?.person === true
/** A comic-book hero (D-168): a made-up character with a name and a pronoun. */
export const isHero = (x) => x?.hero === true
/** A screen character (D-181): made up like a hero, but out of a television show rather than a comic. */
export const isCharacter = (x) => x?.character === true

/**
 * @param {{name: string, proper_noun?: boolean, pronoun?: string, person?: boolean}} x
 * @returns {{name: string, Name: string, subj: string, Subj: string, obj: string, poss: string, Poss: string, refl: string, person: boolean, span: string}}
 *   name   "the lion" / "Brock Lesnar" — mid-sentence
 *   Name   "The lion" / "Brock Lesnar" — at the start of one
 *   subj, Subj, obj, poss, Poss, refl — it, It, it, its, Its, itself; or he, He, him, his, His, himself
 *   span   what a length runs between: "from nose to tail", or "from head to toe" for a person
 */
export function refer(x) {
  const p = PRONOUNS[x?.pronoun] || PRONOUNS.it
  const name = x.proper_noun ? x.name : `the ${x.name.toLowerCase()}`
  return {
    name,
    Name: cap(name),
    subj: p.subj,
    Subj: cap(p.subj),
    obj: p.obj,
    poss: p.poss,
    Poss: cap(p.poss),
    refl: p.refl,
    person: isPerson(x),
    span: isPerson(x) || isHero(x) || isCharacter(x) ? 'from head to toe' : 'from nose to tail',
  }
}

/**
 * "a" or "an" for a word that follows it (D-140): "an open ocean", "an arctic
 * sea", "a eucalyptus forest", "a unicorn". Vowel sounds, not vowel letters:
 * eu- and uni-/use-/ute-/uti- start with a "y", "one" with a "w", and an h that
 * is silent (hour, honest, heir, herb) takes "an".
 */
export function article(word) {
  const w = String(word || '').trim().toLowerCase()
  if (/^(eu|uni|use|usu|ute|uti|ura|one)/.test(w)) return 'a'
  if (/^(hour|honest|heir|herb)/.test(w)) return 'an'
  return /^[aeiou]/.test(w) ? 'an' : 'a'
}

export default { refer, isPerson, isHero, isCharacter, article }
